// components/EditarContactoPedido.js — PEDIDOS-CONTACTO P2 (27-sep-2026) + P3 (28-sep-2026).
// Edita el CONTACTO de UN pedido ya creado (nombre, correo, teléfono, cédula, dirección de envío)
// contra PUT /api/orders/:id/contacto. NO toca líneas, totales, pagos, estado ni stock (el backend
// arma el payload sólo con billing/shipping/meta). Tuteo. Validaciones del POS (celular colombiano,
// depto CO-XXX vía DIVIPOLA). Reusa CityAutocomplete y, para la casilla «también en el cliente»,
// el flujo de «Editar cliente» que YA existe (guardarClienteBatch → PUT /api/clientes/:id).
// P3: si el pedido es de un INVITADO con más pedidos (perfil de la ficha: correo real o celular
// no ambiguo), la casilla «También corregir en sus otros N pedidos» (desmarcada) lleva a una
// VISTA PREVIA (qué pedidos, qué se cambia, avisos de identidad de otra persona) y de ahí al
// lote (PUT /api/orders/:id/contacto/lote), con resultado pedido por pedido. Nunca crea cuenta.
import React, { useEffect, useMemo, useState } from 'react';
import { esCelularColombiano, esCorreoValido } from '../utils/celularColombiano';
import { statusDef, formatOrderDate } from '../utils/orderUtils';
import { codigoDepartamento, esCodigoConocido, nombreDepartamento, DEPARTAMENTOS } from '../data/departamentosColombia';
import CityAutocomplete, { ciudadDesdeSeleccion, ciudadDesdeTextoLibre } from './CityAutocomplete';
import { guardarClienteBatch } from './EditarClienteForm';
import './EditarContactoPedido.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const ESTADOS_DIRECCION = new Set(['pending', 'on-hold', 'processing']);

// integridad-pos (20-oct-2026): la cédula del pedido vive en VARIAS claves según quién la escribió —
// el POS escribe `_cedula`, el editor de contacto `billing_cedula`, y los plugins web las suyas.
// Leer UNA sola clave mostraba «sin cédula» sobre pedidos que SÍ la tienen (#11758 tenía `_cedula`
// y este modal la mostraba vacía). Mismo orden que CEDULA_KEYS del backend (utils/cedulaCliente).
const CEDULA_KEYS_PEDIDO = ['billing_cedula', '_billing_cedula', '_cedula', 'cedula', '_billing_id', 'billing_id', 'billing_cc', 'cc', 'nit', '_billing_nit', 'documento'];
export const cedulaDe = (order) => {
  for (const k of CEDULA_KEYS_PEDIDO) {
    const m = ((order && order.meta_data) || []).find((x) => x && x.key === k && String(x.value || '').trim());
    if (m) return String(m.value).trim();
  }
  return '';
};

// P3: qué se cambia, con las palabras del usuario (la vista previa nunca dice «telefono»).
const LABEL_CAMBIO = {
  nombre: 'nombre', telefono: 'celular', correo: 'correo', cedula: 'cédula',
  direccion_envio: 'dirección de envío', direccion_facturacion: 'dirección de facturación',
};
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

export default function EditarContactoPedido({ order, onGuardado, onCancel }) {
  const b = useMemo(() => order.billing || {}, [order]);
  const s = useMemo(() => order.shipping || {}, [order]);
  const puedeEditarDireccion = ESTADOS_DIRECCION.has(order.status);
  const tieneCuenta = Number(order.customer_id) > 0;

  const [firstName, setFirstName] = useState(b.first_name || '');
  const [lastName, setLastName] = useState(b.last_name || '');
  const [correo, setCorreo] = useState(order.__correoReal || (String(b.email || '').endsWith('@pos.local') ? '' : (b.email || '')));
  const [telefono, setTelefono] = useState(b.phone || '');
  const [cedula, setCedula] = useState(cedulaDe(order));
  const [envio, setEnvio] = useState({
    address_1: s.address_1 || '', address_2: s.address_2 || '',
    city: s.city || '', state: s.state || '', postcode: s.postcode || '',
  });
  const [tambienCliente, setTambienCliente] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  // P3 (solo invitados): perfil con más pedidos, casilla, vista previa y resultado del lote.
  const [perfil, setPerfil] = useState(null); // { otros, pedidos } si el invitado tiene >1 pedido
  const [tambienOtros, setTambienOtros] = useState(false);
  const [vista, setVista] = useState('form'); // form | previa | resultado
  const [previa, setPrevia] = useState(null); // { pedidos, avisos, omitidos }
  const [resultado, setResultado] = useState(null); // { resultados, order }

  // ¿Este invitado tiene MÁS pedidos? (correo real o celular no ambiguo — lo resuelve el backend
  // con la misma regla de la ficha). Si el pedido tiene cuenta o falla la consulta, no hay casilla.
  useEffect(() => {
    if (tieneCuenta) return undefined;
    let vivo = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/orders/${order.id}/contacto/perfil`, { headers: authHeaders() });
        const data = await res.json();
        if (vivo && res.ok && data.invitado && Number(data.otros) > 0) setPerfil(data);
      } catch (_) { /* sin perfil: el formulario sigue editando solo este pedido */ }
    })();
    return () => { vivo = false; };
  }, [order.id, tieneCuenta]);

  const setEnvioCampo = (k, v) => setEnvio((prev) => ({ ...prev, [k]: v }));

  // Ediciones (forma del pedido) SÓLO de lo que cambió.
  const ediciones = useMemo(() => {
    const eds = [];
    if (firstName.trim() !== (b.first_name || '') || lastName.trim() !== (b.last_name || '')) {
      eds.push({ tipo: 'nombre', valorNuevo: { first_name: firstName.trim(), last_name: lastName.trim() } });
    }
    if (correo.trim() && correo.trim() !== (b.email || '')) eds.push({ tipo: 'correo', valorNuevo: correo.trim() });
    if (telefono.trim() && telefono.trim() !== (b.phone || '')) eds.push({ tipo: 'telefono', valorNuevo: telefono.trim() });
    if (cedula.trim() !== cedulaDe(order)) eds.push({ tipo: 'cedula', valorNuevo: cedula.trim() });
    if (puedeEditarDireccion) {
      const cambioEnvio = ['address_1', 'address_2', 'city', 'state', 'postcode'].some((k) => (envio[k] || '') !== (s[k] || ''));
      if (cambioEnvio) eds.push({ tipo: 'direccion_envio', valorNuevo: { ...envio, country: s.country || 'CO' } });
    }
    return eds;
  }, [firstName, lastName, correo, telefono, cedula, envio, b, s, order, puedeEditarDireccion]);

  // Mapea las ediciones del pedido a la forma del CLIENTE (para la casilla «también en el cliente»).
  const edicionesCliente = () => ediciones.map((e) => {
    if (e.tipo === 'telefono') return { tipo: 'telefono', modo: 'editar', valorViejo: b.phone || '', valorNuevo: e.valorNuevo };
    if (e.tipo === 'direccion_envio') return { tipo: 'direccion', modo: 'editar', valorViejo: { address_1: s.address_1 || '' }, valorNuevo: e.valorNuevo };
    return e; // nombre / correo / cedula: mismo shape (SET)
  });

  // P3: pedir la vista previa del lote (solo lectura) y mostrarla antes de escribir nada.
  const verPrevia = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/contacto/lote/previa`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ ediciones }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: data.error || 'No se pudo armar la vista previa.' }); return; }
      setPrevia(data); setVista('previa');
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  // P3: ejecutar el lote (un PUT por pedido en el backend) y mostrar el resultado pedido por pedido.
  const confirmarLote = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/contacto/lote`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ ediciones }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: data.error || 'No se pudo guardar.' }); return; }
      setResultado(data); setVista('resultado');
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const guardar = async () => {
    if (!ediciones.length) { setMsg({ ok: false, text: 'No hay cambios para guardar.' }); return; }
    if (telefono.trim() && !esCelularColombiano(telefono)) { setMsg({ ok: false, text: 'El celular debe tener 10 dígitos y empezar por 3.' }); return; }
    if (correo.trim() && !esCorreoValido(correo)) { setMsg({ ok: false, text: 'El correo no tiene un formato válido.' }); return; }
    // P3: con la casilla marcada, primero la VISTA PREVIA — el lote se confirma desde ahí.
    if (tambienOtros && perfil) { await verPrevia(); return; }
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/contacto`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ ediciones }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: data.error || 'No se pudo guardar.' }); return; }

      // Casilla «también en el cliente»: reusa el flujo existente de Editar cliente. Se reporta APARTE:
      // el pedido ya quedó guardado; si el cliente falla (dato que no coincide), se avisa sin perder eso.
      let notaCliente = '';
      if (tambienCliente && tieneCuenta) {
        try {
          const rc = await guardarClienteBatch(order.customer_id, edicionesCliente(), true);
          notaCliente = rc && rc.ok ? ' También se actualizó la ficha del cliente.' : ` El pedido se guardó, pero la ficha del cliente no: ${(rc && rc.error) || 'revísala'}.`;
        } catch (e) { notaCliente = ` El pedido se guardó, pero la ficha del cliente no: ${e.message}.`; }
      }
      setMsg({ ok: true, text: `Contacto del pedido actualizado.${notaCliente}` });
      if (onGuardado) onGuardado(data.order);
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ecp-overlay" onClick={onCancel}>
      <div className="ecp-panel" onClick={(e) => e.stopPropagation()}>
        {vista === 'form' && (<>
        <div className="ecp-title">Editar contacto del pedido #{order.number}</div>
        <div className="ecp-hint">Corrige los datos de contacto de este pedido. No cambia los productos, el total ni el estado.</div>

        <div className="ecp-row2">
          <label className="ecp-field"><span>Nombre</span><input className="ecp-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} /></label>
          <label className="ecp-field"><span>Apellido</span><input className="ecp-input" value={lastName} onChange={(e) => setLastName(e.target.value)} /></label>
        </div>
        <div className="ecp-row2">
          <label className="ecp-field"><span>Celular</span><input className="ecp-input" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="3001234567" /></label>
          <label className="ecp-field"><span>Cédula</span><input className="ecp-input" value={cedula} onChange={(e) => setCedula(e.target.value)} /></label>
        </div>
        <label className="ecp-field"><span>Correo</span><input className="ecp-input" value={correo} onChange={(e) => setCorreo(e.target.value)} placeholder="correo@ejemplo.com" /></label>

        <div className="ecp-subtitle">Dirección de envío</div>
        {!puedeEditarDireccion && (
          <div className="ecp-warn">La dirección de envío solo se puede editar mientras el pedido no se haya despachado. Este pedido está «{statusDef(order.status).label}»; los demás datos sí puedes corregirlos.</div>
        )}
        <fieldset className="ecp-fieldset" disabled={!puedeEditarDireccion}>
          <input className="ecp-input" placeholder="Calle, carrera, número…" value={envio.address_1} onChange={(e) => setEnvioCampo('address_1', e.target.value)} />
          <input className="ecp-input" placeholder="Apto, barrio, indicaciones (opcional)" value={envio.address_2} onChange={(e) => setEnvioCampo('address_2', e.target.value)} />
          <div className="ecp-row2">
            <CityAutocomplete
              value={envio.city}
              placeholder="Ciudad"
              onPick={(m) => { const sel = ciudadDesdeSeleccion(m); setEnvio((prev) => ({ ...prev, city: sel.city, state: codigoDepartamento(sel.state) })); }}
              onFreeText={(t) => setEnvioCampo('city', ciudadDesdeTextoLibre(t).city)}
            />
            <select className="ecp-input" value={envio.state} onChange={(e) => setEnvioCampo('state', e.target.value)}>
              <option value="">Departamento…</option>
              {DEPARTAMENTOS.map((d) => <option key={d.codigo} value={d.codigo}>{d.nombre}</option>)}
              {envio.state && !esCodigoConocido(envio.state) && <option value={envio.state}>{nombreDepartamento(envio.state) || `${envio.state} (actual)`}</option>}
            </select>
          </div>
        </fieldset>

        {tieneCuenta && (
          <label className="ecp-check">
            <input type="checkbox" checked={tambienCliente} onChange={(e) => setTambienCliente(e.target.checked)} />
            <span>También actualizar estos datos en la ficha del cliente</span>
          </label>
        )}
        {!tieneCuenta && perfil && (
          <label className="ecp-check">
            <input type="checkbox" checked={tambienOtros} onChange={(e) => setTambienOtros(e.target.checked)} />
            <span>También corregir en sus otros {perfil.otros} pedidos</span>
          </label>
        )}

        {msg && <div className={msg.ok ? 'ecp-ok' : 'ecp-error'}>{msg.text}</div>}

        <div className="ecp-actions">
          <button type="button" className="ecp-cancel" disabled={saving} onClick={onCancel}>Cerrar</button>
          {/* Con la casilla del lote marcada el botón NO guarda: abre la vista previa — el texto
              lo dice («Revisar cambios»); guardar de verdad es el confirmar de la previa. */}
          <button type="button" className="ecp-save" disabled={saving || ediciones.length === 0} onClick={guardar}>
            {saving ? (tambienOtros && perfil ? 'Revisando…' : 'Guardando…') : (tambienOtros && perfil ? 'Revisar cambios' : 'Guardar cambios')}
          </button>
        </div>
        </>)}

        {vista === 'previa' && previa && (<>
          <div className="ecp-title">Vas a corregir {previa.pedidos.length} pedidos de este cliente</div>
          <div className="ecp-hint">Revisa qué se cambia en cada pedido antes de guardar. Nada se ha guardado todavía.</div>
          <div className="ecp-previa-list">
            {previa.pedidos.map((p) => (
              <div key={p.id} className="ecp-previa-row">
                <div className="ecp-previa-head">
                  <span className="ecp-previa-num">#{p.number}</span>
                  <span className="ecp-previa-fecha">{formatOrderDate(p.date_created)}</span>
                  <span className="ecp-previa-estado">{statusDef(p.status).label}</span>
                </div>
                <div className="ecp-previa-cambios">
                  Se cambia: {p.cambios.map((c) => LABEL_CAMBIO[c] || c).join(', ') || 'nada'}
                  {p.direccion_sin_cambio && <span className="ecp-previa-sindir"> · dirección sin cambio</span>}
                </div>
              </div>
            ))}
          </div>
          {previa.omitidos > 0 && (
            <div className="ecp-warn">Este cliente tiene más pedidos de los que se pueden corregir de una vez: se corrigen estos {previa.pedidos.length} (los más recientes) y quedan {previa.omitidos} sin cambio.</div>
          )}
          {(previa.avisos || []).map((a) => (
            <div key={a.campo} className="ecp-warn">
              El {a.campo} nuevo ({a.valor}) ya aparece en {a.de.map((d) => `${d.nombre} (${d.tipo === 'cuenta' ? 'cliente con cuenta' : 'otro comprador'})`).join(', ')}.
              Puedes guardarlo igual, pero ten en cuenta que las compras de esa persona y las de este cliente podrían quedar unidas.
            </div>
          ))}
          {msg && <div className={msg.ok ? 'ecp-ok' : 'ecp-error'}>{msg.text}</div>}
          <div className="ecp-actions">
            <button type="button" className="ecp-cancel" disabled={saving} onClick={() => { setVista('form'); setMsg(null); }}>Volver</button>
            <button type="button" className="ecp-save" disabled={saving} onClick={confirmarLote}>{saving ? 'Guardando…' : `Sí, corregir ${previa.pedidos.length} pedidos`}</button>
          </div>
        </>)}

        {vista === 'resultado' && resultado && (<>
          <div className="ecp-title">Resultado de la corrección</div>
          <div className="ecp-hint">{resultado.ok ? 'Todos los pedidos quedaron actualizados.' : 'Algunos pedidos no se pudieron actualizar; los demás sí quedaron.'}</div>
          <div className="ecp-previa-list">
            {(resultado.resultados || []).map((r) => (
              <div key={r.id} className={`ecp-res-row ${r.ok ? 'ecp-res-ok' : 'ecp-res-err'}`}>
                {r.ok
                  ? `✓ Pedido #${r.number}${r.sin_cambios ? ' · sin cambios que aplicar' : ' actualizado'}`
                  : `✗ Pedido #${r.number} · ${r.error || 'no se pudo actualizar'}`}
              </div>
            ))}
          </div>
          <div className="ecp-actions">
            <button type="button" className="ecp-save" onClick={() => { if (onGuardado) onGuardado(resultado.order || null); }}>Listo</button>
          </div>
        </>)}
      </div>
    </div>
  );
}
